import {
  RedoShopifyCountryCode,
  tryCoalesceStringToRedoShopifyCountryCode,
} from "@redotech/redo-inventory-common/fulfillment-country-code";
import {
  type ParsedAddress,
  tryParseAddressLocally,
} from "@redotech/redo-inventory-common/local-address-parser";
import { normalizeAddressProvince } from "./address-province";

export interface ParsedManualOrderAddress {
  name: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  zip: string;
  countryCode?: RedoShopifyCountryCode;
  email: string;
  phone: string;
}

export interface ManualOrderAddressParseRequest {
  address: string;
  name: string;
  email: string;
  phone: string;
}

const EMAIL_LINE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PO_BOX = /p\.?\s*o\.?\s*box/i;
const US_ZIP_ONLY = /^\d{5}(?:-\d{4})?$/;
const STREET_KEYWORD =
  /\b(?:street|st|avenue|ave|road|rd|lane|ln|boulevard|blvd|drive|dr|court|ct|way|suite|ste|apt|apartment|unit|floor|highway|hwy|parkway|pkwy|place|terrace|circle|cir)\b/i;

/** Without this a pasted phone number is the first line with a digit, so it becomes address line 1. */
function isContactLine(line: string): boolean {
  if (EMAIL_LINE.test(line)) {
    return true;
  }
  if (US_ZIP_ONLY.test(line)) {
    return false;
  }
  const digits = line.replace(/\D/g, "");
  return digits.length >= 7 && !/[a-z]/i.test(line);
}

function looksLikePersonName(line: string): boolean {
  if (/\d/.test(line) || line.includes(",")) {
    return false;
  }
  if (STREET_KEYWORD.test(line) || PO_BOX.test(line)) {
    return false;
  }
  const words = line.split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length <= 4;
}

export function getManualOrderAddressParseRequest(
  value: string,
): ManualOrderAddressParseRequest | null {
  const allLines = value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const contactLines = allLines.filter(isContactLine);
  const contact = {
    email: contactLines.find((line) => EMAIL_LINE.test(line)) ?? "",
    phone: contactLines.find((line) => !EMAIL_LINE.test(line)) ?? "",
  };
  const lines = allLines.filter((line) => !isContactLine(line));
  const firstStreetLine = lines.findIndex(isAddressLine);

  if (firstStreetLine > 0) {
    return {
      ...contact,
      address: lines.slice(firstStreetLine).join(", "),
      name: lines.slice(0, firstStreetLine).join(" "),
    };
  }

  // No line carries a house number; take a name-shaped first line rather than
  // folding it into the address.
  const firstLine = lines[0];
  if (
    firstStreetLine === -1 &&
    lines.length > 1 &&
    firstLine &&
    looksLikePersonName(firstLine)
  ) {
    return { ...contact, address: lines.slice(1).join(", "), name: firstLine };
  }

  const address = lines.join(", ").trim();
  return address ? { ...contact, address, name: "" } : null;
}

export function parseManualOrderAddress(
  value: string,
  parsedAddress?: ParsedAddress | null,
): ParsedManualOrderAddress | null {
  const request = getManualOrderAddressParseRequest(value);
  if (!request) {
    return null;
  }

  const parsed = parsedAddress ?? tryParseAddressLocally(request.address);
  // The RPC fills unparseable addresses with blank components rather than
  // failing, and applying those would wipe whatever is already in the form.
  if (!parsed || !parsed.street1.trim()) {
    return null;
  }

  const countryCode = toCountryCode(parsed.country);

  return {
    name: request.name,
    address1: parsed.street1,
    address2: parsed.street2,
    city: parsed.city,
    province: normalizeAddressProvince(parsed.state, countryCode),
    zip: parsed.postalCode,
    countryCode,
    email: request.email,
    phone: request.phone,
  };
}

function toCountryCode(country: string): RedoShopifyCountryCode | undefined {
  return tryCoalesceStringToRedoShopifyCountryCode(
    country.trim().toUpperCase() === "UK" ? "GB" : country,
  );
}

function isAddressLine(line: string): boolean {
  return /^\d+\b|^p\.?\s*o\.?\s*box\b/i.test(line);
}
